import React from 'react';
import { Alert, Box, Checkbox, FormControlLabel, Paper, Typography } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import {
	formatMerchantNumberDisplay,
	getRecommendedMerchantRule,
	isMerchantRuleExpired,
	lookupMerchantNumbers,
	lookupMerchantNumbersForCarrierAndCard,
} from 'features/authorization/helpers/authorizationGuide';
import { MerchantRulesTable } from 'features/authorization/components/MerchantRulesTable';
import { MerchantNumberRule } from 'features/authorization/constants/merchantNumbers';
import { CopyableFieldRow } from './CopyableFieldRow';

interface MerchantNumberLookupProps {
	carrierCode: string;
	cardStamp: string;
	fee: number;
	onCopied?: (message: string) => void;
	/** Las reglas de la base o la tabla fija de respaldo (`useMerchantRules`); sin ellas, la tabla fija. */
	rules?: readonly MerchantNumberRule[];
	/** Si ninguna regla de la base admite las cuotas: la confirmación de supervisión, que el modal exige para seguir. */
	supervisionConfirmed?: boolean;
	onSupervisionConfirmedChange?: (confirmed: boolean) => void;
}

export const MerchantNumberLookup = ({
	carrierCode,
	cardStamp,
	fee,
	onCopied,
	rules,
	supervisionConfirmed = false,
	onSupervisionConfirmedChange,
}: MerchantNumberLookupProps): JSX.Element | null => {
	if (!carrierCode) return null;

	const matchingFee = lookupMerchantNumbers(carrierCode, cardStamp, fee, rules);
	const allForCard = lookupMerchantNumbersForCarrierAndCard(carrierCode, cardStamp, rules);
	const recommended = getRecommendedMerchantRule(carrierCode, cardStamp, fee, rules);
	const displayRules = matchingFee.length > 0 ? matchingFee : allForCard;

	if (displayRules.length === 0) {
		return (
			<Alert severity="warning" sx={{ mt: 1 }}>
				No hay número de comercio configurado para este validating carrier y sello de tarjeta. Consultá con supervisión.
			</Alert>
		);
	}

	const recommendedExpired = recommended ? isMerchantRuleExpired(recommended.validUntil) : false;
	// Solo con las reglas de la base: hay reglas del sello pero ninguna admite estas cuotas (la tabla fija propone igual
	// la primera del sello).
	const noneAdmits = !recommended;
	const installments = `${fee} ${fee === 1 ? 'cuota' : 'cuotas'}`;

	return (
		<Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mt: 1 }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
				<StorefrontIcon color="primary" fontSize="small" />
				<Typography variant="subtitle2" fontWeight="bold">
					Número de comercio
				</Typography>
			</Box>

			{noneAdmits && (
				<>
					<Alert severity="warning" sx={{ mb: 1.5 }}>
						Ninguna regla de {carrierCode.toUpperCase()} admite {allForCard[0].cardLabel} en {installments}. Consultá
						con supervisión.
					</Alert>
					{onSupervisionConfirmedChange && (
						<FormControlLabel
							control={
								<Checkbox
									checked={supervisionConfirmed}
									onChange={(event) => onSupervisionConfirmedChange(event.target.checked)}
								/>
							}
							label={<Typography variant="body2">Supervisión autorizó estas cuotas</Typography>}
							sx={{ mb: 1 }}
						/>
					)}
					<Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
						Las reglas de ese sello, con sus cuotas
					</Typography>
					<MerchantRulesTable rules={allForCard} highlightFee={fee} showNotes={false} />
				</>
			)}

			{recommended && recommended.merchantNumber !== 'GDS_ONLY' && recommended.merchantNumber !== 'NOT_ACCEPTED' && (
				<Box sx={{ mb: 2 }}>
					<Typography variant="caption" color="text.secondary" display="block" gutterBottom>
						Recomendado para {fee} cuota(s)
					</Typography>
					<CopyableFieldRow label="Cód. de comercio" value={recommended.merchantNumber} onCopied={onCopied} />
					{recommendedExpired && (
						<Alert severity="error" sx={{ mt: 1 }}>
							La validez del comercio ({recommended.validUntil}) está vencida. Verificá antes de autorizar.
						</Alert>
					)}
					{recommended.notes && (
						<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
							{recommended.notes}
						</Typography>
					)}
				</Box>
			)}

			{(recommended?.merchantNumber === 'GDS_ONLY' || recommended?.merchantNumber === 'NOT_ACCEPTED') && (
				<Alert severity={recommended.merchantNumber === 'NOT_ACCEPTED' ? 'error' : 'info'} sx={{ mb: 2 }}>
					{formatMerchantNumberDisplay(recommended.merchantNumber)}
					{recommended.notes ? ` — ${recommended.notes}` : ''}
				</Alert>
			)}

			{!noneAdmits && displayRules.length > 1 && (
				<>
					<Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
						Tabla de referencia (fila resaltada = cuotas actuales si aplica)
					</Typography>
					<MerchantRulesTable rules={allForCard} highlightFee={fee} showNotes={false} />
				</>
			)}
		</Paper>
	);
};
